import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { getDashboardLiveTrendData, getDashboardLiveTrendLatestData, getIsDashboardLiveTrendFetching } from "../../model/slice/customDashboardSlice";
import PropTypes from "prop-types";
import { COLORS } from "../../constants/colors";
import { CHANNEL_TYPE } from "../../constants/channel/config";
import { DATA_GAP } from "../../constants/chart/chart";

const MAX_POINTS = 2000; 
const TRIM_BATCH = 200; 
const FLUSH_INTERVAL = 1000; 

const toSeriesRows = (baseData, chNos) => {
  const perCh = new Map();
  if (!baseData?.data?.length || !chNos) return perCh;

  for (const sample of baseData.data) {
    if (!sample?.data?.length) continue;
    const x = Date.parse(sample.dateTime);
    for (const { chNo, value } of sample.data) {
      if (!chNos.has(chNo)) continue;
      const key = String(chNo);
      let arr = perCh.get(key);
      if (!arr) perCh.set(key, (arr = []));
      arr.push({ x, y: Number(value) });
    }
  }
  return perCh;
};

export const LiveChart = (props) => {
  const { item, index, setChartRef, customLines } = props;

  const initialData = useSelector(getDashboardLiveTrendData);
  const latestData = useSelector(getDashboardLiveTrendLatestData);
  const isFetching = useSelector(getIsDashboardLiveTrendFetching);

  const [chartReady, setChartReady] = useState(false);
  const [channelTypes, setChannelTypes] = useState({ analog: new Set() });

  const liveChartRef = useRef(null); 
  const chNosRef = useRef(null); 
  const seriesMapRef = useRef(new Map()); 
  const dataRef = useRef(new Map()); 
  const bufferRef = useRef(new Map()); 

  useEffect(() => {
    if (
      isFetching[item.id] === undefined ||
      initialData[item.id] === undefined ||
      isFetching[item.id] ||
      initialData[item.id].length === 0
    ) return;

    const anChnls = initialData[item.id].config.type.reduce((acc, val, idx) => {
      if (val === CHANNEL_TYPE.ANALOG) acc.push(idx);
      return acc;
    }, []);

    const anChNosSet = new Set(anChnls);
    const analogChNos = new Set(item.chNos.filter((chNo, idx) => anChNosSet.has(idx)));
    chNosRef.current = analogChNos;

    const perCh = toSeriesRows(initialData[item.id], analogChNos);
    perCh.forEach((rows, key) => {
      if (rows.length > MAX_POINTS) perCh.set(key, rows.slice(-MAX_POINTS));
    });

    dataRef.current = perCh;
    bufferRef.current.clear();

    setChannelTypes({ analog: analogChNos });
    setChartReady(analogChNos.size > 0 && perCh.size > 0);
  }, [isFetching[item.id]]);

  useEffect(() => {
    const perCh = toSeriesRows(latestData[item.id], chNosRef.current);
    perCh.forEach((rows, key) => {
      const buf = bufferRef.current.get(key);
      if (buf) {
        for (const r of rows) buf.push(r);
      } else {
        bufferRef.current.set(key, rows);
      }
    });
  }, [latestData[item.id]]);

  useEffect(() => {
    const flush = () => {
      const buffer = bufferRef.current;
      if (!buffer.size) return;

      const chart = liveChartRef.current;
      const canDraw = !!chart && !chart.isDisposed() && !document.hidden;

      buffer.forEach((rows, key) => {
        let arr = dataRef.current.get(key);
        if (!arr) dataRef.current.set(key, (arr = []));

        const lastX = arr.length ? arr[arr.length - 1].x : -Infinity;
        const fresh = rows.filter((r) => r.x > lastX);
        if (!fresh.length) return;

        for (const r of fresh) arr.push(r);

        const needTrim = arr.length > MAX_POINTS + TRIM_BATCH;
        if (needTrim) arr.splice(0, arr.length - MAX_POINTS);

        const series = seriesMapRef.current.get(key);
        if (!canDraw || !series) return;

        if (needTrim) series.data.setAll(arr.slice());
        else series.data.pushAll(fresh);
      });

      buffer.clear();
    };

    const onVisibility = () => {
      if (document.hidden) return;
      seriesMapRef.current.forEach((series, key) => {
        series.data.setAll((dataRef.current.get(key) ?? []).slice());
      });
    };

    const id = setInterval(flush, FLUSH_INTERVAL);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useLayoutEffect(() => {
    if (isFetching[item.id] || !chartReady) return;

    // *** License From 2023/05/22 ***
    am5.addLicense(process.env.AMCHARTS_LICENSE);
    // ******

    const root = createAnalogChart();

    return () => {
      liveChartRef.current = null; 
      seriesMapRef.current.clear(); 
      root.dispose();
    };
  }, [chartReady, customLines, isFetching[item.id]]);

  useEffect(() => {
    const width = Number(item.thickness) || 2;
    seriesMapRef.current.forEach((series) => {
      series.strokes.template.set("strokeWidth", width);
    });
  }, [item.thickness, chartReady, customLines]);

  const createAnalogChart = () => {
    const root = am5.Root.new(`chartdiv-${index}`);
    root.fps = 30; 

    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      focusable: true,
      panX: true,
      panY: true,
      wheelX: "panX",
      wheelY: "zoomX",
      maxTooltipDistance: -1,
    }));
    chart.set("animationDuration", 0);

    const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
      maxDeviation: 0.5,
      extraMin: -0.1,
      extraMax: 0.1,
      baseInterval: {
        timeUnit: "second",
        count: 1,
      },
      renderer: am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
        minGridDistance: 60,
      }),
      tooltip: am5.Tooltip.new(root, {}),
    }));

    const xRenderer = xAxis.get("renderer");
    xRenderer.labels.template.setAll({
      fill: am5.color(COLORS.white),
      opacity: 0.2,
      fontSize: "12px",
      paddingTop: 14,
      centerX: am5.percent(20),
    });
    xRenderer.grid.template.setAll({
      stroke: am5.color(COLORS.white),
      strokeWidth: 2,
      strokeDasharray: [4, 2],
    });

    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, {}),
    }));

    const yRenderer = yAxis.get("renderer");
    yRenderer.labels.template.setAll({
      fill: am5.color(COLORS.white),
      opacity: 0.2,
      fontSize: "14px",
    });
    yRenderer.grid.template.setAll({
      stroke: am5.color(COLORS.white),
      strokeWidth: 2,
      strokeDasharray: [4, 2],
    });

    seriesMapRef.current.clear();

    const createSeries = (chNo) => {
      const key = String(chNo);
      const config = initialData[item.id].config;
      const channelNumberIndex = config.chNo.findIndex((v) => v === chNo);
      const channelName = config.chName[channelNumberIndex];
      const unit = config.unit[channelNumberIndex];
      const color = customLines?.find((c) => c.chNo == chNo)?.color;

      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: key,
          xAxis,
          yAxis,
          valueXField: "x",
          valueYField: "y",
          interactionsEnabled: true,
          minDistance: 1,
          connect: false,
          autoGapCount: DATA_GAP,
          tooltip: am5.Tooltip.new(root, {
            labelText: `${channelName}: {valueY} ${unit}`,
            pointerOrientation: "vertical",
          }),
        })
      );

      if (color) {
        series.setAll({
          stroke: am5.color(color),
          fill: am5.color(color),
        });
      }

      series.strokes.template.set("strokeWidth", Number(item.thickness) || 2);

      series.data.setAll((dataRef.current.get(key) ?? []).slice());
      seriesMapRef.current.set(key, series);
    };

    channelTypes.analog.forEach((chNo) => createSeries(chNo));

    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
      xAxis,
    }));
    cursor.lineY.set("visible", false);

    liveChartRef.current = chart;
    setChartRef(liveChartRef);

    return root;
  };

  return (
    <Box id={`chartdiv-${index}`} sx={{ height: "calc(100% - 30px)" }} />
  );
};

LiveChart.propTypes = {
  item: PropTypes.object,
  chNos: PropTypes.array,
  index: PropTypes.number,
  setChartRef: PropTypes.func,
  customLines: PropTypes.array,
};